import { Link, useSearchParams } from "react-router-dom";
import { useApi } from "@/lib/useApi";
import { VideoGrid } from "@/components/VideoRow";
import { resolveUrl } from "@/lib/api";
import { joinStyles } from "@/lib/format";

const Heading = ({ children }) => (
  <h2 className="text-[19px] md:text-[22px] lg:text-[24px] 4xl:text-[28px] font-bold leading-tight mb-4 md:mb-5">{children}</h2>
);

const StyleBox = ({ style }) => (
  <Link
    to={`/styles/${style.slug}`}
    className="w-[144px] h-[72px] shrink-0 rounded-xl bg-[#D9D9D9] overflow-hidden flex items-center justify-center text-[13px] font-semibold text-[#333] hover:scale-[1.03] transition-transform duration-200"
    data-testid={`results-style-box-${style.slug}`}
  >
    {style.image_url ? <img src={resolveUrl(style.image_url)} alt={style.name} className="w-full h-full object-cover" /> : <span className="px-2 text-center">{style.name}</span>}
  </Link>
);

const FestivalCard = ({ festival }) => (
  <Link to={`/festivals/${festival.id}`} className="w-[144px] shrink-0 group" data-testid={`results-festival-card-${festival.id}`}>
    <div className="h-[182px] rounded-xl bg-[#D9D9D9] overflow-hidden group-hover:scale-[1.03] transition-transform duration-200">
      {(festival.cover_image_url || festival.logo_url) && (
        <img src={resolveUrl(festival.cover_image_url || festival.logo_url)} alt={festival.name} className="w-full h-full object-cover" />
      )}
    </div>
    <p className="mt-3 text-[14px] font-bold leading-tight truncate">{festival.name}</p>
    <p className="text-[11px] font-medium text-[#8A8A8A] truncate">{joinStyles(festival.dance_styles)}</p>
  </Link>
);

export default function SearchResults() {
  const [params] = useSearchParams();
  const q = (params.get("q") || "").trim();
  const { data, loading } = useApi(q ? `/search?q=${encodeURIComponent(q)}` : null);
  const empty = data && !data.videos.length && !data.festivals.length && !data.dance_styles.length;

  return (
    <div className="mt-8 md:mt-10 lg:mt-12 pb-16 df-fade-up" data-testid="search-results-page">
      <h1 className="text-[22px] md:text-[26px] lg:text-[28px] 4xl:text-[34px] font-bold leading-tight" data-testid="search-results-query">
        {q ? `Results for “${q}”` : "Search"}
      </h1>
      {data && (
        <p className="mt-1.5 text-[14px] font-medium text-[#8A8A8A]" data-testid="search-results-count">
          {data.video_total} performance{data.video_total === 1 ? "" : "s"}
          {data.festivals.length ? ` · ${data.festivals.length} festival${data.festivals.length === 1 ? "" : "s"}` : ""}
          {data.dance_styles.length ? ` · ${data.dance_styles.length} dance style${data.dance_styles.length === 1 ? "" : "s"}` : ""}
        </p>
      )}

      {!q && <p className="mt-10 text-[16px] text-[#8A8A8A]">Type something in the search bar to see results.</p>}
      {q && loading && <p className="mt-10 text-[16px] text-[#8A8A8A]">Searching…</p>}
      {empty && (
        <p className="mt-10 text-[16px] text-[#8A8A8A]" data-testid="search-results-empty">
          No results found. Try another festival, dance style or performance.
        </p>
      )}

      {data?.dance_styles?.length > 0 && (
        <section className="mt-10" data-testid="results-dance-styles">
          <Heading>Dance Styles</Heading>
          <div className="flex gap-4 md:gap-5 flex-wrap">
            {data.dance_styles.map((s) => (
              <StyleBox key={s.id} style={s} />
            ))}
          </div>
        </section>
      )}

      {data?.festivals?.length > 0 && (
        <section className="mt-10" data-testid="results-festivals">
          <Heading>Festivals</Heading>
          <div className="flex gap-4 md:gap-5 flex-wrap">
            {data.festivals.map((f) => (
              <FestivalCard key={f.id} festival={f} />
            ))}
          </div>
        </section>
      )}

      {data?.videos?.length > 0 && (
        <section className="mt-10" data-testid="results-performances">
          <Heading>Performances</Heading>
          <VideoGrid videos={data.videos} />
        </section>
      )}
    </div>
  );
}
